import api from "../services/authService";

/**
 * Shared SOC / Fee Allocation Notice review queue API
 * (documents/views/fee_notice_views.py on the fee-notice backend stack —
 * see .cursor/agent-handoff-fee-notice-frontend.md for the full contract).
 *
 * Every endpoint returns errors as `{ error: "..." }` (never `message`),
 * unlike the mail-intake API this feature otherwise mirrors — the
 * `error.response?.data?.message` fallback below is kept only so a
 * generic axios/network failure still resolves to a readable string.
 */

const raise = (error, fallback) => {
  const message =
    error.response?.data?.error || error.response?.data?.message || fallback;
  // ``code`` lets a caller act on a refusal rather than only display it —
  // the confirm gate uses it to send the reviewer to the tab that can
  // clear the block. Absent on most responses; callers read `.message`.
  throw {
    message,
    code: error.response?.data?.code,
    status: error.response?.status || 500,
  };
};

export const fetchFeeNoticeListAPI = async (
  scope = "active",
  { offset, limit } = {}
) => {
  try {
    const params = new URLSearchParams({ scope });
    if (Number.isInteger(offset)) params.set("offset", String(offset));
    if (Number.isInteger(limit)) params.set("limit", String(limit));
    return await api.get(`/fee-notices/?${params.toString()}`);
  } catch (error) {
    return raise(error, "Failed to fetch the fee notice queue");
  }
};

// Super-admin only (role admin/super_admin); the backend answers 403
// for everyone else, which the slice surfaces as a plain error.
export const fetchFeeNoticeTeamActivityAPI = async (days = 7) => {
  try {
    return await api.get(
      `/fee-notices/team-activity/?days=${encodeURIComponent(days)}`
    );
  } catch (error) {
    return raise(error, "Failed to load team activity");
  }
};

// The letters behind one dashboard cell: agent x metric (confirmed /
// dismissed / invoices / sf_writes) over the same window the summary
// used, so the modal's rows always match the number that was clicked.
export const fetchFeeNoticeTeamActivityDetailAPI = async ({
  agentId,
  metric,
  days = 7,
}) => {
  try {
    const params = new URLSearchParams({
      agent_id: String(agentId),
      metric,
      days: String(days),
    });
    return await api.get(`/fee-notices/team-activity/detail/?${params}`);
  } catch (error) {
    return raise(error, "Failed to load activity detail");
  }
};

export const claimFeeNoticeAPI = async (id) => {
  try {
    return await api.post(`/fee-notices/${id}/claim/`);
  } catch (error) {
    return raise(error, "Could not open this letter");
  }
};

export const releaseFeeNoticeAPI = async (id) => {
  try {
    return await api.post(`/fee-notices/${id}/release/`);
  } catch (error) {
    return raise(error, "Failed to release this letter");
  }
};

export const confirmFeeNoticeMatterAPI = async (id, matterId) => {
  try {
    return await api.post(`/fee-notices/${id}/matter/`, {
      matter_id: matterId,
    });
  } catch (error) {
    return raise(error, "Failed to confirm the matter");
  }
};

export const fetchFeeNoticeContextAPI = async (id) => {
  try {
    return await api.get(`/fee-notices/${id}/context/`);
  } catch (error) {
    return raise(error, "Failed to load the review sidebar");
  }
};

export const confirmFeeNoticeAPI = async (id, fields, noFeeAwarded) => {
  try {
    // no_fee_awarded records the fact of the FAN/SOC in DMS with no
    // dollars and no Salesforce write (there is nothing to write).
    const body = noFeeAwarded
      ? { fields, no_fee_awarded: true }
      : { fields };
    return await api.post(`/fee-notices/${id}/confirm/`, body);
  } catch (error) {
    return raise(error, "Failed to confirm this letter");
  }
};

export const dismissFeeNoticeAPI = async (id, reason = "") => {
  try {
    return await api.post(`/fee-notices/${id}/dismiss/`, { reason });
  } catch (error) {
    return raise(error, "Failed to dismiss this letter");
  }
};

export const removeFeeNoticeAPI = async (id) => {
  try {
    return await api.post(`/fee-notices/${id}/remove/`);
  } catch (error) {
    return raise(error, "Failed to remove this letter from the queue");
  }
};

// --------------------------------------------------------------------------
// Cost-reimbursement invoice (backend #1479/#1480). GET returns the saved
// payload when one exists, else a fresh assembly from the letter's data +
// the matter's live Salesforce cost ledger; POST saves reviewer edits (the
// server recomputes total_costs from the cost lines).
// --------------------------------------------------------------------------

export const fetchFeeNoticeInvoiceAPI = async (id) => {
  try {
    return await api.get(`/fee-notices/${id}/invoice/`);
  } catch (error) {
    return raise(error, "Failed to load the invoice");
  }
};

export const saveFeeNoticeInvoiceAPI = async (id, invoice) => {
  try {
    return await api.post(`/fee-notices/${id}/invoice/`, { invoice });
  } catch (error) {
    return raise(error, "Failed to save the invoice");
  }
};

// Accepts unsaved edits (`invoice`) so Download always reflects what's on
// screen; with no argument the backend renders the saved/fresh payload.
export const downloadFeeNoticeInvoicePdfAPI = async (id, invoice = null) => {
  try {
    return await api.post(
      `/fee-notices/${id}/invoice/pdf/`,
      invoice ? { invoice } : {},
      { responseType: "blob", timeout: 60000 }
    );
  } catch (error) {
    return raise(error, "Failed to generate the invoice PDF");
  }
};

export const sendFeeNoticeInvoiceAPI = async (id, to = "") => {
  try {
    return await api.post(
      `/fee-notices/${id}/invoice/send/`,
      to ? { to } : {}
    );
  } catch (error) {
    return raise(error, "Failed to send the invoice");
  }
};
